import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { HardDrive, Wifi, AlertTriangle, Settings2, Check, ImageOff } from 'lucide-react';
import { toast } from 'sonner';
import { adminFetch } from '../utils/adminFetch';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const barColor = (percent, alert) => {
  if (alert || percent >= 90) return 'bg-red-500';
  if (percent >= 75) return 'bg-amber-500';
  return 'bg-green-500';
};

const UsageBar = ({ icon: Icon, label, used, limit, free, percent, alert, threshold, testid }) => (
  <div className="space-y-2" data-testid={testid}>
    <div className="flex items-center justify-between text-sm">
      <span className="flex items-center gap-2 font-medium">
        <Icon className="w-4 h-4" />
        {label}
      </span>
      <span className={`text-xs ${alert ? 'text-red-500 font-bold' : 'text-muted-foreground'}`}>
        {used} / {limit} GB
      </span>
    </div>
    <div className="h-2 bg-muted rounded-full overflow-hidden">
      <div
        className={`h-full ${barColor(percent, alert)} transition-all duration-500`}
        style={{ width: `${Math.min(percent, 100)}%` }}
      />
    </div>
    <div className="flex items-center justify-between text-xs">
      <span className="text-muted-foreground">{percent}% usado</span>
      <span className={alert ? 'text-red-500 font-medium flex items-center gap-1' : 'text-muted-foreground'}>
        {alert && <AlertTriangle className="w-3 h-3" />}
        {free} GB libres
        {alert && ` (umbral ${threshold} GB)`}
      </span>
    </div>
  </div>
);

export const CloudinaryUsageWidget = () => {
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [threshold, setThreshold] = useState(3);
  const [saving, setSaving] = useState(false);
  const [brokenCount, setBrokenCount] = useState(null);

  const fetchUsage = async () => {
    try {
      const res = await adminFetch(`${API}/admin/cloudinary/usage`);
      if (res.ok) {
        const data = await res.json();
        setUsage(data);
        setThreshold(data.alert_threshold_gb);
      }
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  };

  const fetchBroken = async () => {
    try {
      const res = await adminFetch(`${API}/admin/cloudinary/broken-scan`);
      if (res.ok) {
        const data = await res.json();
        setBrokenCount(data.total_broken || 0);
      }
    } catch { /* silent */ }
  };

  useEffect(() => {
    fetchUsage();
    fetchBroken();
  }, []);

  const saveThreshold = async () => {
    setSaving(true);
    try {
      const res = await adminFetch(`${API}/admin/cloudinary/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ alert_threshold_gb: threshold }),
      });
      if (res.ok) {
        toast.success('Umbral guardado');
        setShowSettings(false);
        fetchUsage();
      } else {
        toast.error('Error al guardar');
      }
    } catch {
      toast.error('Error de conexión');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-card border border-border p-5 animate-pulse">
        <div className="h-4 w-40 bg-muted mb-4" />
        <div className="h-2 bg-muted mb-2" />
        <div className="h-2 bg-muted" />
      </div>
    );
  }

  if (!usage) {
    return (
      <div className="bg-card border border-border p-5" data-testid="cloudinary-widget-error">
        <p className="text-sm text-muted-foreground">No se pudo obtener el uso de Cloudinary. Verifica las credenciales.</p>
      </div>
    );
  }

  const anyAlert = usage.storage.alert || usage.bandwidth.alert;

  return (
    <div
      className={`bg-card border p-5 ${anyAlert ? 'border-red-500/50' : 'border-border'}`}
      data-testid="cloudinary-widget"
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-medium flex items-center gap-2">
            Almacenamiento Cloudinary
            <span className="text-[10px] bg-muted px-2 py-0.5 rounded uppercase tracking-widest text-muted-foreground">
              {usage.plan}
            </span>
          </h3>
          <p className="text-xs text-muted-foreground">
            {usage.resources.toLocaleString('es-ES')} recursos
          </p>
        </div>
        <button
          onClick={() => setShowSettings(!showSettings)}
          className="p-2 text-muted-foreground hover:text-foreground transition-colors"
          title="Configurar umbral de alerta"
          data-testid="cloudinary-settings-toggle"
        >
          <Settings2 className="w-4 h-4" />
        </button>
      </div>

      <div className="space-y-5">
        <UsageBar
          icon={HardDrive}
          label="Almacenamiento"
          used={usage.storage.used_gb}
          limit={usage.storage.limit_gb}
          free={usage.storage.free_gb}
          percent={usage.storage.percent}
          alert={usage.storage.alert}
          threshold={usage.alert_threshold_gb}
          testid="cloudinary-storage-bar"
        />
        <UsageBar
          icon={Wifi}
          label="Ancho de banda mensual"
          used={usage.bandwidth.used_gb}
          limit={usage.bandwidth.limit_gb}
          free={usage.bandwidth.free_gb}
          percent={usage.bandwidth.percent}
          alert={usage.bandwidth.alert}
          threshold={usage.alert_threshold_gb}
          testid="cloudinary-bandwidth-bar"
        />
      </div>

      {anyAlert && (
        <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 text-sm text-red-600 dark:text-red-400 flex items-start gap-2" data-testid="cloudinary-alert">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium">Espacio bajo</p>
            <p className="text-xs opacity-90">
              Elimina galerías o archivos antiguos antes de subir nuevo contenido.
            </p>
          </div>
        </div>
      )}

      {showSettings && (
        <div className="mt-4 pt-4 border-t border-border space-y-3">
          <label className="text-sm font-medium block">
            Avisarme cuando queden menos de: <strong>{threshold} GB</strong>
          </label>
          <input
            type="range"
            min={1}
            max={10}
            step={0.5}
            value={threshold}
            onChange={(e) => setThreshold(parseFloat(e.target.value))}
            className="w-full accent-foreground"
            data-testid="cloudinary-threshold-slider"
          />
          <div className="flex justify-between text-[10px] text-muted-foreground">
            <span>1 GB</span>
            <span>5 GB</span>
            <span>10 GB</span>
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={saveThreshold}
              disabled={saving}
              className="gap-2"
              data-testid="cloudinary-save-threshold"
            >
              <Check className="w-3.5 h-3.5" />
              {saving ? 'Guardando...' : 'Guardar'}
            </Button>
            <Button size="sm" variant="outline" onClick={() => setShowSettings(false)}>
              Cancelar
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CloudinaryUsageWidget;
